import img1 from '../images/layouts/blog-01.png';
import img2 from '../images/layouts/blog-02.png';

const dataBlog = [
  {
    id: 1,
    slug: 'zustand-vs-redux-toolkit-2025',
    img: img1,
    title: 'Zustand vs Redux Toolkit: Why I Switched for Enterprise Apps in 2025',
    time: 'March 10, 2025',
    category: 'State Management',
    readTime: '8 min read',
    text: 'After years of Redux, I made the switch to Zustand in production. Here\'s the honest breakdown — what broke, what got faster, and when Redux still wins.',
    content: [
      {
        type: 'intro',
        text: 'I have been writing Redux since 2018. Redux Toolkit made it bearable. But after building a complex enterprise SOAR platform — with dozens of slices, deeply nested async flows, and real-time WebSocket updates — I hit a wall. The boilerplate was manageable. The mental overhead was not. This is the story of why I switched to Zustand, what I learned, and when I would go back.',
      },
      {
        type: 'heading',
        text: 'The Problem With Redux Toolkit at Scale',
      },
      {
        type: 'paragraph',
        text: 'Redux Toolkit (RTK) solved the verbosity problem. createSlice, createAsyncThunk, RTK Query — these are genuinely good APIs. But as the application grew, three friction points kept surfacing:',
      },
      {
        type: 'list',
        items: [
          'Every new feature required touching at minimum 3 files: slice, selector, and the component itself.',
          'Middleware chains got complex fast — especially when combining RTK Query with custom WebSocket middleware.',
          'Re-renders were hard to trace. Selectors helped, but improperly memoized selectors caused entire subtrees to re-render on every dispatch.',
        ],
      },
      {
        type: 'paragraph',
        text: 'The turning point was a dashboard that had to stay live-updated via WebSockets while also handling complex filter state, pagination, and optimistic updates. The Redux version worked — but it took two developers a week to debug a re-render cascade that was freezing the UI on every WebSocket message.',
      },
      {
        type: 'heading',
        text: 'What Zustand Actually Is',
      },
      {
        type: 'paragraph',
        text: 'Zustand is not Redux-lite. It is a fundamentally different mental model. Instead of a single global store with reducers, you create small, focused stores with direct mutation-style setters. There is no dispatch, no action creators, no middleware boilerplate by default.',
      },
      {
        type: 'code',
        text: `// Redux Toolkit approach
const counterSlice = createSlice({
  name: 'counter',
  initialState: { value: 0 },
  reducers: {
    increment: (state) => { state.value += 1 },
  },
});
export const { increment } = counterSlice.actions;
export default counterSlice.reducer;

// Zustand approach
const useCounterStore = create((set) => ({
  value: 0,
  increment: () => set((state) => ({ value: state.value + 1 })),
}));`,
      },
      {
        type: 'paragraph',
        text: 'The Zustand version is a single file. No provider needed. No combineReducers. You just call the hook wherever you need it.',
      },
      {
        type: 'heading',
        text: 'Real-World Migration: The SOAR Platform',
      },
      {
        type: 'paragraph',
        text: 'When I started building the Playbook Builder — a node-based visual workflow editor for security automation — I chose Zustand from day one. The state was inherently complex: nodes, edges, execution state, selected elements, undo history. In Redux this would have been a 400-line slice. In Zustand:',
      },
      {
        type: 'code',
        text: `const usePlaybookStore = create((set, get) => ({
  nodes: [],
  edges: [],
  selectedNode: null,

  addNode: (node) =>
    set((state) => ({ nodes: [...state.nodes, node] })),

  updateNodeData: (id, data) =>
    set((state) => ({
      nodes: state.nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...data } } : n
      ),
    })),

  setSelectedNode: (id) =>
    set({ selectedNode: id }),

  reset: () =>
    set({ nodes: [], edges: [], selectedNode: null }),
}));`,
      },
      {
        type: 'paragraph',
        text: 'The result: 60% less code, zero provider wrapping, and — critically — components only re-render when the specific slice of state they subscribe to changes. I used the selector pattern to get granular subscriptions:',
      },
      {
        type: 'code',
        text: `// Only re-renders when nodes array changes
const nodes = usePlaybookStore((state) => state.nodes);

// Only re-renders when selectedNode changes
const selected = usePlaybookStore((state) => state.selectedNode);`,
      },
      {
        type: 'heading',
        text: 'Performance: The Real Numbers',
      },
      {
        type: 'paragraph',
        text: 'After migrating the dashboard from Redux to Zustand, I profiled both versions using React DevTools. The results were clear:',
      },
      {
        type: 'list',
        items: [
          'Average render time on a WebSocket update: reduced from 18ms to 4ms.',
          'Number of components re-rendering per state change: dropped from 23 to 6.',
          'Time to identify and fix a re-render bug: from hours to minutes, because the store is co-located and readable.',
        ],
      },
      {
        type: 'quote',
        text: 'Zustand\'s selective subscription model means components only pay for the state they actually use. Redux with selectors can achieve the same — but Zustand makes it the default, not an optimisation you have to remember.',
      },
      {
        type: 'heading',
        text: 'When I Would Still Use Redux Toolkit',
      },
      {
        type: 'paragraph',
        text: 'Zustand is not the answer to everything. Redux Toolkit still wins in specific scenarios:',
      },
      {
        type: 'list',
        items: [
          'RTK Query is genuinely excellent for server state — caching, invalidation, polling, optimistic updates. If your app is API-heavy, RTK Query alone is worth the Redux dependency.',
          'Large teams with mixed experience levels benefit from Redux\'s enforced patterns. The structure is a feature when onboarding developers who are unfamiliar with the codebase.',
          'Redux DevTools time-travel debugging is unmatched for reproducing complex interaction bugs. Zustand has devtools support but it is more limited.',
        ],
      },
      {
        type: 'heading',
        text: 'The Honest Verdict',
      },
      {
        type: 'paragraph',
        text: 'For complex client-side state in a single product team — Zustand wins. Less code, faster renders, easier debugging. For server state — use React Query or RTK Query regardless of what you choose for client state. They solve different problems. For large teams or highly regulated environments where enforcement of patterns matters — Redux Toolkit is still the right call.',
      },
      {
        type: 'paragraph',
        text: 'The switch to Zustand was not a philosophical decision. It was a practical one. And in 2025, for the kind of real-time enterprise interfaces I build, it has been the right one.',
      },
    ],
    tags: ['React', 'Zustand', 'Redux', 'State Management', 'Performance'],
  },
  {
    id: 2,
    slug: 'react-dashboard-100k-rows-optimization',
    img: img2,
    title: 'How I Optimized a React Dashboard Handling 100k+ Rows Without UI Lag',
    time: 'February 3, 2025',
    category: 'Performance',
    readTime: '10 min read',
    text: 'When your data table freezes the browser with 100,000 rows, setTimeout is not the answer. Here is the real optimization stack I used to get to 60fps on a live security dashboard.',
    content: [
      {
        type: 'intro',
        text: 'The brief seemed straightforward: build a dashboard where security analysts could monitor alerts, devices, and events across an enterprise network. What nobody mentioned in the initial scope was that "events" meant up to 100,000 rows, live-updated via WebSockets, with complex filtering and sorting that had to happen instantly. The first prototype was unusable. Here is how I fixed it.',
      },
      {
        type: 'heading',
        text: 'The Problem: What 100k Rows Actually Means for the DOM',
      },
      {
        type: 'paragraph',
        text: 'Before optimizing, I needed to understand why it was slow. The initial implementation rendered all rows into the DOM. On a dataset of 100,000 items, that is 100,000 DOM nodes — each potentially with 8-12 cells. Chrome\'s rendering engine simply cannot paint, layout, and composite that many nodes at 60 frames per second. The tab would lock for 3-4 seconds on initial load and freeze completely on any filter change.',
      },
      {
        type: 'paragraph',
        text: 'The profiler told the full story: 87% of frame time was being spent in layout and paint. The JavaScript was actually fast — the problem was the DOM.',
      },
      {
        type: 'heading',
        text: 'Fix 1: Virtual Scrolling with react-window',
      },
      {
        type: 'paragraph',
        text: 'Virtual scrolling (also called windowing) is the single highest-impact optimization for large lists. Instead of rendering all 100,000 rows, you only render the rows currently visible in the viewport — typically 15-30 rows. As the user scrolls, rows are recycled.',
      },
      {
        type: 'code',
        text: `import { FixedSizeList as List } from 'react-window';

const Row = ({ index, style, data }) => (
  <div style={style} className="table-row">
    <span>{data[index].id}</span>
    <span>{data[index].severity}</span>
    <span>{data[index].timestamp}</span>
    <span>{data[index].device}</span>
  </div>
);

function AlertTable({ alerts }) {
  return (
    <List
      height={600}
      itemCount={alerts.length}
      itemSize={48}
      itemData={alerts}
      width="100%"
    >
      {Row}
    </List>
  );
}`,
      },
      {
        type: 'paragraph',
        text: 'Result: Initial render time dropped from 3,800ms to 41ms. The DOM node count went from 800,000+ to under 400. This one change fixed 80% of the problem.',
      },
      {
        type: 'heading',
        text: 'Fix 2: Move Filtering and Sorting Off the Main Thread',
      },
      {
        type: 'paragraph',
        text: 'Even with virtual scrolling, filtering 100,000 items on the main thread caused noticeable jank — especially with complex multi-column filters. The fix was to move the computation to a Web Worker.',
      },
      {
        type: 'code',
        text: `// worker.js
self.onmessage = ({ data: { rows, filters, sortConfig } }) => {
  let result = rows;

  if (filters.severity) {
    result = result.filter((r) => r.severity === filters.severity);
  }
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (r) =>
        r.device.toLowerCase().includes(q) ||
        r.event.toLowerCase().includes(q)
    );
  }
  if (sortConfig.key) {
    result = [...result].sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      return a[sortConfig.key] > b[sortConfig.key] ? dir : -dir;
    });
  }

  self.postMessage(result);
};

// In the component
const workerRef = useRef(null);

useEffect(() => {
  workerRef.current = new Worker(
    new URL('./worker.js', import.meta.url)
  );
  workerRef.current.onmessage = ({ data }) => setFilteredRows(data);
  return () => workerRef.current.terminate();
}, []);

const applyFilters = useCallback((filters) => {
  workerRef.current.postMessage({ rows: allRows, filters, sortConfig });
}, [allRows, sortConfig]);`,
      },
      {
        type: 'paragraph',
        text: 'With the computation off the main thread, the UI remains fully responsive while filtering. The filter result appears within 80-120ms on a dataset of 100,000 rows — imperceptible to the user.',
      },
      {
        type: 'heading',
        text: 'Fix 3: Memoize Everything That Touches the Data',
      },
      {
        type: 'paragraph',
        text: 'With WebSocket updates coming in every few seconds, the component re-render cycle needed to be airtight. Three rules I enforced across the dashboard:',
      },
      {
        type: 'code',
        text: `// 1. Memoize the row component — prevents re-rendering unchanged rows
const Row = memo(({ index, style, data }) => { ... });

// 2. Memoize expensive derived data
const criticalAlerts = useMemo(
  () => rows.filter((r) => r.severity === 'critical'),
  [rows]
);

// 3. Stabilize callbacks passed to child components
const handleRowClick = useCallback(
  (id) => setSelectedRow(id),
  [] // No dependency — setSelectedRow is stable
);`,
      },
      {
        type: 'heading',
        text: 'Fix 4: Throttle WebSocket Updates',
      },
      {
        type: 'paragraph',
        text: 'The final piece was the most counterintuitive. WebSocket messages were arriving at up to 50 updates per second during high-activity periods. Applying each update individually meant 50 re-renders per second — killing performance even with all the above fixes in place.',
      },
      {
        type: 'paragraph',
        text: 'The solution was to batch WebSocket updates and flush them at a controlled rate:',
      },
      {
        type: 'code',
        text: `const pendingUpdates = useRef([]);

useEffect(() => {
  const flush = setInterval(() => {
    if (pendingUpdates.current.length === 0) return;

    setRows((prev) => {
      const updated = [...prev];
      for (const update of pendingUpdates.current) {
        const idx = updated.findIndex((r) => r.id === update.id);
        if (idx !== -1) updated[idx] = { ...updated[idx], ...update };
        else updated.push(update);
      }
      pendingUpdates.current = [];
      return updated;
    });
  }, 100); // Flush at 10fps max

  socket.on('alert', (data) => {
    pendingUpdates.current.push(data);
  });

  return () => {
    clearInterval(flush);
    socket.off('alert');
  };
}, []);`,
      },
      {
        type: 'paragraph',
        text: 'Batching at 100ms intervals means the UI updates 10 times per second maximum — which is more than fast enough for human perception — while keeping the render cycle under control.',
      },
      {
        type: 'heading',
        text: 'The Final Numbers',
      },
      {
        type: 'list',
        items: [
          'Initial render: 3,800ms → 41ms (99% improvement)',
          'Filter operation on 100k rows: 1,200ms → 90ms (off main thread)',
          'WebSocket-driven re-renders: 50/sec → 10/sec (batched)',
          'DOM nodes: 800,000+ → under 400 (virtual scrolling)',
          'Frame rate during scroll: consistently 60fps',
        ],
      },
      {
        type: 'quote',
        text: 'Performance optimization is not about clever tricks. It is about understanding where time is actually being spent — DOM, JavaScript, or network — and attacking the biggest bottleneck first. In almost every large table problem I have seen, the DOM is the bottleneck, not the JavaScript.',
      },
      {
        type: 'heading',
        text: 'The Stack in Summary',
      },
      {
        type: 'paragraph',
        text: 'If you are building a high-performance data table in React in 2025, this is the stack: react-window for virtual scrolling, Web Workers for off-thread computation, React.memo + useMemo + useCallback for render control, and batched updates for any real-time data source. Each layer addresses a different bottleneck. All four together is what gets you to 60fps on 100,000 rows.',
      },
    ],
    tags: ['React', 'Performance', 'WebSockets', 'Optimization', 'Virtual Scrolling'],
  },
];

export default dataBlog;
